import { AuthData } from "api/resources/auth/types"
import { transformKeys } from "api/utils"
import camelCase from "lodash/camelCase"
import { BROWSER_STORAGE_KEYS, IS_LOCALHOST, USER_TYPE } from "utils/constants"

import { deleteCookie, getCookie, setCookie } from "./cookieUtils"
import { getPlatformURL } from "./helpers"
import { routes } from "./routes"

const REFRESH_TOKEN_MAX_AGE_SECONDS = 60 * 60 * 24 * 365 * 10

export class AuthManager {
  private static storageKey = BROWSER_STORAGE_KEYS.auth

  static saveAuthInfo(authInfo: AuthData) {
    setCookie(this.storageKey, JSON.stringify(authInfo), {
      maxAge: REFRESH_TOKEN_MAX_AGE_SECONDS,
      path: "/",
      sameSite: "Lax",
      secure: window.location.protocol === "https:",
    })
  }

  static getAuthInfo() {
    const rawAuthInfo = getCookie(this.storageKey)

    // One-time migration: if cookie is absent, try to read legacy auth info from localStorage.
    try {
      if (localStorage) {
        const localStorageAuth = localStorage.getItem(this.storageKey)
        if (localStorageAuth) {
          const legacyAuth: AuthData = transformKeys(
            JSON.parse(localStorageAuth),
            camelCase
          )

          // In case of shadow login, prefer migrated auth info over existing cookie to avoid forcing users to re-authenticate.
          if (legacyAuth.shadow) {
            this.saveAuthInfo(legacyAuth)
            localStorage.removeItem(this.storageKey)
            return legacyAuth
          }

          // Persist migrated auth info into the cookie using existing options.
          if (!rawAuthInfo) {
            this.saveAuthInfo(legacyAuth)
            localStorage.removeItem(this.storageKey)
            return legacyAuth
          }
        }
      }
    } catch (error) {
      // Ignore migration errors and fall through to returning null.
    }

    if (rawAuthInfo)
      try {
        const parsed = JSON.parse(decodeURIComponent(rawAuthInfo))
        const newAuthInfoObj: AuthData = transformKeys(parsed, camelCase)
        return newAuthInfoObj
      } catch (error) {
        return null
      }

    return null
  }

  static clearAuthInfo() {
    deleteCookie(this.storageKey)
    try {
      localStorage.removeItem(this.storageKey)
    } catch (error) {
      // Ignore localStorage errors
    }
  }

  static redirectToLogin() {
    console.log("[AuthManager] Redirecting to login")
    const url = new URL(getPlatformURL("sso", `/`))
    url.searchParams.append("platform", USER_TYPE)
    url.searchParams.append("redirect-url", window.location.href)
    window.location.href = url.toString()
    // console.log("[AuthManager] Redirect URL:", url.toString())
  }

  static logout() {
    this.clearAuthInfo()
    const url = new URL(getPlatformURL("sso", `/logout`))
    url.searchParams.append("platform", USER_TYPE)
    url.searchParams.append("redirect-url", window.location.href)
    window.location.href = url.toString()
    // console.log("[AuthManager] Redirect URL:", url.toString())
  }

  /**
   * Development tool to invalidate the access token while keeping the refresh token intact.
   * This forces the token refresh flow to trigger on the next API call.
   * @returns boolean - true if auth was invalidated, false if no auth exists
   */
  static __invalidateAuth() {
    const authInfo = this.getAuthInfo()

    if (!authInfo) {
      console.warn("[AuthManager] No auth info found to invalidate")
      return false
    }

    const invalidatedAuthInfo = {
      ...authInfo,
      accessToken: "_INVALIDATED_" + authInfo.accessToken,
    }

    this.saveAuthInfo(invalidatedAuthInfo)
    console.log(
      "[AuthManager] Access token invalidated. Next API call will trigger token refresh."
    )
    return true
  }
}

if (IS_LOCALHOST) {
  // @ts-expect-error this is safe to do
  window.AuthManager = AuthManager
}
